import { useState } from 'react';
import {
  Box,
  Stack,
  Typography,
  TextField,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete,
  IconButton,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableRow
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import DescriptionIcon from '@mui/icons-material/Description';
import InventoryIcon from '@mui/icons-material/Inventory';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import BusinessIcon from '@mui/icons-material/Business';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import { StyledButton } from '../../../components';

interface ASN {
  id: string;
  ssccId: string | null;
  poNumber: string;
  supplier: string;
  destination: string;
  packageCount: number;
  status: string;
}

export default function ASNManagement() {
  const [searchQuery, setSearchQuery] = useState('');
  const [asnResults, setAsnResults] = useState<ASN | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [openAddToSSCCDialog, setOpenAddToSSCCDialog] = useState(false);
  const [openRemoveFromSSCCDialog, setOpenRemoveFromSSCCDialog] = useState(false);
  const [selectedSSCC, setSelectedSSCC] = useState('');
  const [removeReason, setRemoveReason] = useState('');
  const [availableSSCCs] = useState(['SSCC-123', 'SSCC-456', 'SSCC-789']);

  const handleSearch = async () => {
    setIsLoading(true);
    // TODO: Implement actual search logic
    setTimeout(() => {
      // Mock data for demonstration
      setAsnResults({
        id: 'ASN-001',
        ssccId: 'SSCC-123',
        poNumber: 'PO-12345',
        supplier: 'Acme Corp',
        destination: 'Perth DC',
        packageCount: 3,
        status: 'Receipted'
      });
      setIsLoading(false);
    }, 500);
  };

  const handleAddToSSCC = async () => {
    if (!asnResults || !selectedSSCC) return;

    // TODO: Implement actual add logic
    console.log('Adding ASN:', asnResults.id, 'to SSCC:', selectedSSCC);

    setAsnResults({ ...asnResults, ssccId: selectedSSCC });
    setOpenAddToSSCCDialog(false);
    setSelectedSSCC('');
  };

  const handleRemoveFromSSCC = async () => {
    if (!asnResults || !removeReason) return;

    // TODO: Implement actual remove logic
    console.log('Removing ASN:', asnResults.id, 'from SSCC, reason:', removeReason);

    setAsnResults({ ...asnResults, ssccId: null });
    setOpenRemoveFromSSCCDialog(false);
    setRemoveReason('');
  };

  return (
    <Stack spacing={3} sx={{ px: 3 }}>
      {/* Search Section */}
      <Card sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
        <CardContent>
          <Stack spacing={2}>
            <Typography variant="h6" fontWeight={600}>
              Search ASN
            </Typography>
            <Stack direction="row" spacing={2}>
              <TextField
                fullWidth
                placeholder="Enter ASN ID or PO Number..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                InputProps={{
                  startAdornment: <SearchIcon sx={{ color: 'text.secondary', mr: 1 }} />
                }}
              />
              <StyledButton
                variant="contained"
                onClick={handleSearch}
                disabled={isLoading}
                startIcon={<SearchIcon />}
                sx={{ minWidth: 120 }}>
                {isLoading ? 'Searching...' : 'Search'}
              </StyledButton>
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      {/* Results Section */}
      {asnResults && (
        <Card sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
          <CardContent>
            <Stack spacing={3}>
              {/* Header */}
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Stack direction="row" spacing={2} alignItems="center">
                  <DescriptionIcon sx={{ fontSize: 32, color: 'primary.main' }} />
                  <Box>
                    <Typography variant="h6" fontWeight={600}>
                      {asnResults.id}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {asnResults.packageCount} packages
                    </Typography>
                  </Box>
                </Stack>
                <Chip label={asnResults.status} color="success" />
              </Stack>

              <Divider />

              {/* ASN Details */}
              <Box>
                <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 2 }}>
                  Details
                </Typography>
                <Table size="small">
                  <TableBody>
                    <TableRow>
                      <TableCell sx={{ borderBottom: 'none', pl: 0, color: 'text.secondary' }}>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <DescriptionIcon sx={{ fontSize: 16 }} />
                          <span>PO Number</span>
                        </Stack>
                      </TableCell>
                      <TableCell sx={{ borderBottom: 'none', fontWeight: 600 }}>{asnResults.poNumber}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ borderBottom: 'none', pl: 0, color: 'text.secondary' }}>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <BusinessIcon sx={{ fontSize: 16 }} />
                          <span>Supplier</span>
                        </Stack>
                      </TableCell>
                      <TableCell sx={{ borderBottom: 'none', fontWeight: 600 }}>{asnResults.supplier}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ borderBottom: 'none', pl: 0, color: 'text.secondary' }}>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <LocalShippingIcon sx={{ fontSize: 16 }} />
                          <span>Destination</span>
                        </Stack>
                      </TableCell>
                      <TableCell sx={{ borderBottom: 'none', fontWeight: 600 }}>{asnResults.destination}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </Box>

              <Divider />

              {/* SSCC Info */}
              <Box>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                  <InventoryIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
                  <Typography variant="subtitle2" fontWeight={600}>
                    SSCC Assignment
                  </Typography>
                </Stack>
                {asnResults.ssccId ? (
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Chip label={asnResults.ssccId} sx={{ backgroundColor: 'success.lighter' }} />
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => setOpenRemoveFromSSCCDialog(true)}
                      title="Remove from SSCC">
                      <RemoveCircleOutlineIcon />
                    </IconButton>
                  </Stack>
                ) : (
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Typography variant="body2" color="text.secondary">
                      Not assigned to any SSCC
                    </Typography>
                    <StyledButton
                      variant="outlined"
                      size="small"
                      startIcon={<AddCircleOutlineIcon />}
                      onClick={() => setOpenAddToSSCCDialog(true)}>
                      Add to SSCC
                    </StyledButton>
                  </Stack>
                )}
              </Box>
            </Stack>
          </CardContent>
        </Card>
      )}

      {/* No Results */}
      {!asnResults && searchQuery && !isLoading && (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <DescriptionIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            No ASN found
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Try searching with a different ASN ID or PO Number
          </Typography>
        </Box>
      )}

      {/* Add to SSCC Dialog */}
      <Dialog open={openAddToSSCCDialog} onClose={() => setOpenAddToSSCCDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add ASN to SSCC</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 2 }}>
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                ASN ID
              </Typography>
              <Typography variant="body1" fontWeight={600}>
                {asnResults?.id}
              </Typography>
            </Box>
            <Autocomplete
              options={availableSSCCs}
              value={selectedSSCC}
              onChange={(_, newValue) => setSelectedSSCC(newValue || '')}
              renderInput={(params) => <TextField {...params} label="Select SSCC" placeholder="Search..." />}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={() => setOpenAddToSSCCDialog(false)}>Cancel</Button>
          <StyledButton
            variant="contained"
            onClick={handleAddToSSCC}
            disabled={!selectedSSCC}
            startIcon={<CheckCircleIcon />}>
            Add to SSCC
          </StyledButton>
        </DialogActions>
      </Dialog>

      {/* Remove from SSCC Dialog */}
      <Dialog
        open={openRemoveFromSSCCDialog}
        onClose={() => setOpenRemoveFromSSCCDialog(false)}
        maxWidth="sm"
        fullWidth>
        <DialogTitle>Remove ASN from SSCC</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 2 }}>
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                ASN ID
              </Typography>
              <Typography variant="body1" fontWeight={600}>
                {asnResults?.id}
              </Typography>
            </Box>
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Current SSCC
              </Typography>
              <Chip label={asnResults?.ssccId} size="small" color="primary" />
            </Box>
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Reason for Removal *"
              value={removeReason}
              onChange={(e) => setRemoveReason(e.target.value)}
              placeholder="Enter the reason for removing this ASN from the SSCC..."
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={() => setOpenRemoveFromSSCCDialog(false)}>Cancel</Button>
          <StyledButton
            variant="contained"
            color="error"
            onClick={handleRemoveFromSSCC}
            disabled={!removeReason}
            startIcon={<RemoveCircleOutlineIcon />}>
            Remove from SSCC
          </StyledButton>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
